// Revisado: 2025-12-24 [I18n Scan]
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const SLOT_COLORS = [
    { bg: 'bg-red-500', border: 'border-red-500', text: 'text-red-600', shadow: 'shadow-red-500/50' },
    { bg: 'bg-blue-500', border: 'border-blue-500', text: 'text-blue-600', shadow: 'shadow-blue-500/50' },
    { bg: 'bg-emerald-500', border: 'border-emerald-500', text: 'text-emerald-600', shadow: 'shadow-emerald-500/50' },
    { bg: 'bg-orange-500', border: 'border-orange-500', text: 'text-orange-600', shadow: 'shadow-orange-500/50' },
    { bg: 'bg-violet-500', border: 'border-violet-500', text: 'text-violet-600', shadow: 'shadow-violet-500/50' },
    { bg: 'bg-cyan-500', border: 'border-cyan-500', text: 'text-cyan-600', shadow: 'shadow-cyan-500/50' }
];

const SupportDeckBonus = ({ slots, data }) => {
    const { t } = useTranslation();

    const bonuses = useMemo(() => {
        if (!data || !slots.some(s => s.support)) return [];

        // Map: Effect Name -> { total: number, contributions: Array<{val, slotIndex, isUnique}> }
        const effectMap = {};

        slots.forEach((slot, slotIndex) => {
            if (!slot.support) return;

            // Lookup full card data
            // data.matrix is likely keyed by ID string
            const cardId = slot.support.support_id?.toString() || slot.support.id?.toString();
            const matrixCard = data.matrix[cardId];

            if (!matrixCard) return;

            const capLevel = slot.capLevel; // 0 to 4

            // Helper to add contribution
            const addContrib = (name, value, isUnique) => {
                if (!effectMap[name]) {
                    effectMap[name] = { total: 0, contributions: [] };
                }
                effectMap[name].total += value;
                effectMap[name].contributions.push({
                    value,
                    slotIndex,
                    isUnique
                });
            };

            // 1. Process Normal Effects
            if (matrixCard.effects) {
                Object.entries(matrixCard.effects).forEach(([effectName, values]) => {
                    const valAtCap = parseFloat(values[capLevel]);
                    if (!isNaN(valAtCap) && valAtCap > 0) {
                        addContrib(effectName, valAtCap, false);
                    }
                });
            }

            // 2. Process Unique Effects
            // We assume Unique is active if card is present (Simplification for Builder)
            if (matrixCard.unique_effects) {
                matrixCard.unique_effects.forEach(u => {
                    const parsed = parseFloat(u.value);
                    if (!isNaN(parsed)) {
                        addContrib(u.description, parsed, true);
                    }
                });
            }
        });

        // Convert to array and Sort
        return Object.entries(effectMap)
            .map(([effect, data]) => ({
                effect,
                total: data.total,
                contributions: data.contributions.sort((a, b) => a.slotIndex - b.slotIndex)
            }))
            .sort((a, b) => a.effect.localeCompare(b.effect));

    }, [slots, data]);

    if (!bonuses.length) return null;

    return (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden mt-6 mb-6">
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-4 flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-full backdrop-blur-sm">
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                </div>
                <h3 className="text-lg font-bold text-white">{t('SupportDeckBonus.title')}</h3>
            </div>

            <div className="p-0 overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400 font-medium border-b border-slate-100 dark:border-slate-700">
                        <tr>
                            <th className="px-6 py-4 w-1/3">{t('SupportDeckBonus.effect')}</th>
                            <th className="px-6 py-4 w-2/3">{t('SupportDeckBonus.total_bonus')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                        {bonuses.map((row, idx) => (
                            <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                                <td className="px-6 py-3 font-medium text-slate-700 dark:text-slate-200">
                                    {row.effect}
                                </td>
                                <td className="px-6 py-3">
                                    <div className="flex items-center gap-4 flex-wrap">
                                        {/* Total Badge */}
                                        <span className="inline-flex items-center justify-center px-4 py-1.5 bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-white font-mono font-bold text-lg rounded-lg border border-slate-200 dark:border-slate-600 min-w-[3rem]">
                                            {Number.isInteger(row.total) ? row.total : row.total.toFixed(1)}
                                        </span>

                                        {/* Breakdown Pills */}
                                        <div className="flex flex-wrap gap-2 items-center">
                                            {row.contributions.map((contrib, cIdx) => {
                                                const colors = SLOT_COLORS[contrib.slotIndex % SLOT_COLORS.length];
                                                const isUnique = contrib.isUnique;

                                                return (
                                                    <span
                                                        key={cIdx}
                                                        className={`
                                                            inline-flex items-center px-2 py-0.5 rounded text-xs font-bold shadow-sm transition-transform hover:scale-105 cursor-default border
                                                            ${isUnique
                                                                ? `bg-amber-100 dark:bg-amber-900/30 ${colors.border} text-slate-900 dark:text-amber-100 border-2`
                                                                : `bg-transparent ${colors.border} ${colors.text}`
                                                            }
                                                        `}
                                                        title={`Slot ${contrib.slotIndex + 1}${isUnique ? ' (Unique Bonus)' : ''}`}
                                                    >
                                                        {isUnique && <span className="mr-0.5 text-[10px]">★</span>}
                                                        {Number.isInteger(contrib.value) ? contrib.value : contrib.value.toFixed(1)}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {bonuses.length > 0 && (
                <div className="p-3 bg-slate-50 dark:bg-slate-900/30 text-center text-xs text-slate-400 border-t border-slate-100 dark:border-slate-700">
                    {t('SupportDeckBonus.footer_note')}
                </div>
            )}
        </div>
    );
};
export default SupportDeckBonus;
